import pillWarning from '@/assets/figma/accounts/3d-pill-warning.png';
import type { TradeStatusCounts } from '@/entities/business-relationship/api/businessRelationship.api';
import type { RelationshipGroup } from '@/entities/business-relationship/lib/statusView';
import { Subtitle } from '@/shared/components/Subtitle';
import { Button } from '@/shared/components/ui/button';
import { Icon } from '@/shared/components/ui/icon';
import { cn } from '@/shared/lib/utils';

import { AppOnlyActionButton } from './AppOnlyActionButton';

const COUNT_CARDS: Array<{
  tab: RelationshipGroup;
  label: string;
  dot: string;
}> = [
  { tab: 'ongoing', label: '거래중', dot: 'bg-green-500' },
  { tab: 'applying', label: '필터링 신청', dot: 'bg-yellow-500' },
  { tab: 'closed', label: '종료·거절', dot: 'bg-grey-400' },
];

/**
 * M-04 · M-05 거래 정보 (Figma 의약품 495:6425) — 웹에서는 조회만 한다.
 * 의원급은 제약사, 병원급은 의약품 단위로 센다 (상세 tradeStatusCounts).
 * - 「N건이 추가 필터링 가능해요」(3D아이콘 메뉴 495:6466): [목록 보기] → M-22 드로어. 0건이면 숨긴다 (Figma 주석 886:13010)
 * - 상태별 숫자 칸: 누르면 M-19 · M-20 거래 정보 드로어를 그 탭으로 연다
 * - [필터링 신청] · [추가 필터링]: 앱 전용 (X6)
 */
export const TradeInfoSection = ({
  isHospital,
  counts,
  untradedApplicableCount,
  onOpenTrade,
  onOpenMissed,
}: {
  isHospital: boolean;
  counts: TradeStatusCounts;
  untradedApplicableCount: number;
  onOpenTrade: (tab: RelationshipGroup) => void;
  onOpenMissed: () => void;
}) => {
  const unit = isHospital ? '의약품' : '제약사';

  return (
    <section className="flex flex-col gap-4 px-6">
      <Subtitle
        title={`${unit} 거래 정보`}
        description={
          isHospital
            ? '병원 거래처는 의약품 단위로 거래해요. 웹에서는 조회만 할 수 있어요.'
            : '의원급 거래처는 제약사 단위로 거래해요. 웹에서는 조회만 할 수 있어요.'
        }
        actions={
          <>
            <AppOnlyActionButton feature="filteringApply">
              필터링 신청
            </AppOnlyActionButton>
            <AppOnlyActionButton feature="additionalFiltering">
              추가 필터링
            </AppOnlyActionButton>
          </>
        }
      />
      {untradedApplicableCount > 0 && (
        <div className="flex items-center gap-2 rounded-20 bg-yellow-50 px-4 py-3">
          <img src={pillWarning} alt="" className="size-10 shrink-0" />
          <p className="min-w-0 flex-1 text-body-16-sb text-fg-primary">
            {untradedApplicableCount}건이 추가 필터링 가능해요
          </p>
          <Button
            variant="text"
            size="small"
            className="text-fg-tertiary hover:bg-yellow-500/10"
            rightIcon={<Icon name="nav-arrow-right-line-16" />}
            onClick={onOpenMissed}
          >
            목록 보기
          </Button>
        </div>
      )}
      <div className="grid grid-cols-3 gap-2 lg:gap-6">
        {COUNT_CARDS.map((c) => (
          <button
            type="button"
            key={c.tab}
            onClick={() => onOpenTrade(c.tab)}
            aria-label={`${c.label} ${unit} ${counts[c.tab]}건 보기`}
            className="flex flex-col items-center gap-2 rounded-20 p-4 transition-colors outline-none hover:bg-surface-grey focus-visible:shadow-[0_0_0_3px_var(--color-brand-200)] active:bg-grey-100"
          >
            <span className="text-heading-20-b text-fg-primary">
              {counts[c.tab]}
            </span>
            <span className="flex items-center gap-2 text-body-14-m whitespace-nowrap text-fg-primary">
              <span className={cn('size-2 rounded-full', c.dot)} aria-hidden />
              {c.label}
            </span>
          </button>
        ))}
      </div>
    </section>
  );
};
